import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { BaseFunctionCreator } from '../function.model';
import { TANGENT_FN_META, type TangentFnParams } from './tangent-fn.model';

/** Tangent function params with index signature for JsxParamStore compatibility */
type TangentFnParamsIndexed = TangentFnParams & { [key: string]: number };

/**
 * Creates tangent functions: a * tan(b(x - c)) + d
 *
 * Parameters:
 * - a: amplitude (vertical stretch)
 * - b: frequency (affects period = π/b)
 * - c: phase shift (horizontal translation)
 * - d: vertical shift
 *
 * Note: This is named 'tangent-fn' to avoid confusion with the 'tangent' feature
 * which draws tangent lines to curves.
 */
export class TangentFnCreator extends BaseFunctionCreator<TangentFnParamsIndexed> {
  readonly functionType = 'tangent-fn';
  readonly defaultParams: TangentFnParamsIndexed = { ...TANGENT_FN_META.defaultParams };
  readonly availableFeatures = TANGENT_FN_META.availableFeatures;
  readonly handleConfigs = TANGENT_FN_META.handleConfigs;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as TangentFnParamsIndexed;

    const colorName = (display?.curve?.color ?? 'red') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    // ------------------------------------------------
    // PARAMETER STORE
    // ------------------------------------------------
    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: TANGENT_FN_META.defaultRanges.a },
      b: { value: initialParams.b, range: TANGENT_FN_META.defaultRanges.b },
      c: { value: initialParams.c, range: TANGENT_FN_META.defaultRanges.c },
      d: { value: initialParams.d, range: TANGENT_FN_META.defaultRanges.d },
    });

    // ------------------------------------------------
    // HANDLE CREATION
    // ------------------------------------------------
    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    // ------------------------------------------------
    // EVALUATE FUNCTION
    // ------------------------------------------------
    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const b = paramStore.getValue('b');
      const c = paramStore.getValue('c');
      const d = paramStore.getValue('d');
      const arg = b * (x - c);
      // Check for asymptotes (where cos(arg) ≈ 0)
      const cosArg = Math.cos(arg);
      if (Math.abs(cosArg) < 1e-10) {
        return NaN;
      }
      return a * Math.tan(arg) + d;
    };

    // ------------------------------------------------
    // CURVE CREATION
    // ------------------------------------------------
    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      style: display?.curve,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];

    // ------------------------------------------------
    // DERIVATIVE
    // ------------------------------------------------
    const getDerivative = (order = 1): ((x: number) => number) => {
      // Order 1 derivative: a * b * sec²(b(x - c)) = a * b / cos²(b(x - c))
      if (order === 1) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          const arg = b * (x - c);
          const cosArg = Math.cos(arg);
          if (Math.abs(cosArg) < 1e-10) {
            return NaN;
          }
          return (a * b) / (cosArg * cosArg);
        };
      }

      // Order 2 derivative: 2 * a * b² * sec²(b(x-c)) * tan(b(x-c))
      if (order === 2) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          const arg = b * (x - c);
          const cosArg = Math.cos(arg);
          if (Math.abs(cosArg) < 1e-10) {
            return NaN;
          }
          const tanArg = Math.tan(arg);
          const sec2 = 1 / (cosArg * cosArg);
          return 2 * a * b * b * sec2 * tanArg;
        };
      }

      // For higher orders, use numerical differentiation
      return (x: number) => {
        const h = 1e-5;
        const prevDerivative = getDerivative(order - 1);
        return (prevDerivative(x + h) - prevDerivative(x - h)) / (2 * h);
      };
    };

    return {
      id,
      type: 'tangent-fn',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as TangentFnParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      getDerivative,
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
